import React, { useState, useEffect } from 'react';
import { formatDateDDMMYYYY } from '../utils/dateUtils';
import DashboardLayout from '../components/DashboardLayout';
import ConfirmModal from '../components/ConfirmModal';
import { getAmbulanceTypes, createAmbulanceType, updateAmbulanceType, deleteAmbulanceType, uploadFile } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface AmbulanceType {
  _id: string;
  metadata: {
    name: string;
    displayName?: string;
    createdAt: number;
    updatedAt: number;
    isDeleted: boolean;
  };
  spec: {
    description: string;
    iconUrl?: string;
    tenantId?: string | null;
  };
}

interface AmbulanceTypeManagementProps {
  skipLayout?: boolean;
}

const AmbulanceTypeManagement: React.FC<AmbulanceTypeManagementProps> = ({ skipLayout = false }) => {
  const { hasPermission } = useAuth();
  const [types, setTypes] = useState<AmbulanceType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState<{ id: string; name: string } | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [editingType, setEditingType] = useState<AmbulanceType | null>(null);
  const [viewingType, setViewingType] = useState<AmbulanceType | null>(null);
  const [formData, setFormData] = useState({
    displayName: '',
    description: '',
    iconUrl: '',
  });
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState<{ displayName?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [iconUploading, setIconUploading] = useState(false);
  const [iconUploadError, setIconUploadError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);

  // Close drawer with Escape key
  useEffect(() => {
    if (!showModal) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        const activeElement = document.activeElement as HTMLElement | null;
        if (activeElement && typeof activeElement.blur === 'function') {
          activeElement.blur();
        }
        setShowModal(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showModal]);

  // Capitalize first letter of each word
  const capitalizeWords = (text: string): string => {
    return text
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  useEffect(() => {
    fetchTypes(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce search term - wait 500ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 800);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Refetch when debounced search changes
  useEffect(() => {
    fetchTypes(1, pageSize, debouncedSearchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  const fetchTypes = async (page: number, size: number = pageSize, search: string = debouncedSearchTerm) => {
    try {
      setLoading(true);
      const response = await getAmbulanceTypes(page, size, search && search.trim() ? search.trim() : undefined);
      setTypes(response.data.data || []);

      const pagination = response.data.pagination || {};
      setCurrentPage(pagination.currentPage || page);
      setTotalItems(pagination.totalItems || 0);
      setHasNextPage(pagination.hasNextPage || false);
      setHasPreviousPage(pagination.hasPreviousPage || false);
    } catch (err: any) {
      setError('Failed to load ambulance types');
      console.error('Failed to load ambulance types:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (type?: AmbulanceType) => {
    if (type) {
      setEditingType(type);
      setFormData({
        displayName: type.metadata.displayName || type.metadata.name,
        description: type.spec.description || '',
        iconUrl: type.spec.iconUrl || '',
      });
    } else {
      setEditingType(null);
      setFormData({
        displayName: '',
        description: '',
        iconUrl: '',
      });
    }
    setShowModal(true);
    setError('');
    setValidationErrors({});
    setIconUploadError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    setValidationErrors({});
    setSubmitting(true);

    if (!formData.displayName.trim()) {
      setValidationErrors({ displayName: 'Display name is required' });
      setError('Please fix the highlighted errors.');
      setSubmitting(false);
      return;
    }

    try {
      if (editingType) {
        // When editing, only send displayName (name is immutable)
        const updateData = {
          metadata: {
            displayName: formData.displayName.trim(),
          },
          spec: {
            description: formData.description.trim() || '',
            iconUrl: formData.iconUrl.trim() || undefined,
          },
        };
        await updateAmbulanceType(editingType._id, updateData);
      } else {
        // When creating, send displayName and let backend auto-generate name (slug)
        const data = {
          metadata: {
            displayName: formData.displayName.trim(),
          },
          spec: {
            description: formData.description.trim() || '',
            iconUrl: formData.iconUrl.trim() || undefined,
          },
        };
        await createAmbulanceType(data);
      }

      setShowModal(false);
      fetchTypes(currentPage);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string, displayName: string) => {
    setTypeToDelete({ id, name: displayName });
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!typeToDelete) return;

    try {
      await deleteAmbulanceType(typeToDelete.id);
      const nextPage =
        currentPage > 1 && types.length === 1 ? currentPage - 1 : currentPage;
      fetchTypes(nextPage);
      setShowDeleteConfirm(false);
      setTypeToDelete(null);
    } catch (err: any) {
      setErrorMessage(err.response?.data?.message || 'Failed to delete ambulance type. Unknown error occurred.');
      setShowDeleteConfirm(false);
      setTypeToDelete(null);
      setShowErrorModal(true);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleRefresh = () => {
    fetchTypes(currentPage, pageSize);
  };

  const content = (
    <>
      {/* <div className="page-header">
        <h2>Ambulance Type Management</h2>
        <p>Manage ambulance types for your organization</p>
      </div> */}

      <div className="card">
        <div className="card-header">
          {/* <h3 className="card-title">Ambulance Types</h3> */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', justifyContent: 'flex-end' }}>
            <input
              type="text"
              placeholder="Search ambulance types..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                fontSize: '16px',
                width: '220px',
                flex: '0 0 220px',
              }}
            />
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="refresh-button"
              title="Refresh list"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
              {loading ? 'Loading...' : 'Refresh'}
            </button>
            {hasPermission('ambulancetype.create') && (
              <button className="btn-add" onClick={() => handleOpenModal()}>
                + Add Type
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading ambulance types...</div>
        ) : types.length === 0 ? (
          <div className="empty-state">
            <h3>No ambulance types yet</h3>
            <p>Create your first ambulance type to get started</p>
          </div>
        ) : (
          <div style={{ position: 'relative' }}>
            <div className="table-container" style={{ height: 'calc(100vh - 450px)', overflow: 'auto' }}>
              <table className="bookings-table">
                <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                  <tr>
                    <th>Display Name</th>
                    <th>Icon</th>
                    <th>Description</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {types.map((type) => {
                    const isSystemLevel = type.spec.tenantId === null || type.spec.tenantId === undefined;
                    return (
                      <tr key={type._id}>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <strong>{type.metadata.displayName || type.metadata.name}</strong>
                          {isSystemLevel && (
                            <span
                              style={{
                                marginLeft: '8px',
                                fontSize: '13px',
                                color: '#667eea',
                                fontWeight: 'bold',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                background: '#e8f5e9',
                              }}
                            >
                              🌍 SYSTEM
                            </span>
                          )}
                        </td>
                        <td>
                          {type.spec.iconUrl ? (
                            <img
                              src={type.spec.iconUrl}
                              alt="Type icon"
                              style={{ width: 32, height: 32, objectFit: 'contain' }}
                            />
                          ) : (
                            <span style={{ color: '#999', fontStyle: 'italic' }}>Default</span>
                          )}
                        </td>
                        <td>
                          {type.spec.description ? (
                            (() => {
                              const full = type.spec.description;
                              const maxLen = 140;
                              const truncated =
                                full.length > maxLen ? `${full.slice(0, maxLen).trimEnd()}…` : full;
                              return (
                                <span
                                  title={full}
                                  style={{ cursor: full.length > maxLen ? 'pointer' : 'default' }}
                                >
                                  {truncated}
                                </span>
                              );
                            })()
                          ) : (
                            <span style={{ color: '#999', fontStyle: 'italic' }}>No description</span>
                          )}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {formatDateDDMMYYYY(type.metadata.createdAt)}
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="btn-icon"
                              onClick={() => {
                                setViewingType(type);
                                setShowViewModal(true);
                              }}
                              title="View ambulance type details"
                              style={{
                                marginRight: '8px',
                                padding: '6px 12px',
                                borderRadius: '4px',
                                border: '1px solid #16a34a',
                                background: '#16a34a',
                                color: '#ffffff',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              View
                            </button>
                            {!isSystemLevel && hasPermission('ambulancetype.update') && (
                              <button
                                className="btn-icon btn-edit"
                                onClick={() => handleOpenModal(type)}
                              >
                                Edit
                              </button>
                            )}
                            {!isSystemLevel && hasPermission('ambulancetype.delete') && (
                              <button
                                className="btn-icon btn-delete"
                                onClick={() =>
                                  handleDelete(
                                    type._id,
                                    type.metadata.displayName || type.metadata.name
                                  )
                                }
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

              <div className="pagination-controls" style={{ position: 'relative', width: '100%' }}>
              <div className="pagination-left" style={{ position: 'absolute', right: 250, whiteSpace: 'nowrap', zIndex: 10 }}>
                <label style={{ fontSize: '16px', whiteSpace: 'nowrap' }}>
                  Rows per page:{' '}
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      const newSize = Number(e.target.value);
                      setPageSize(newSize);
                      fetchTypes(1, newSize);
                    }}
                    style={{ minWidth: '60px', width: 'auto' }}
                  >
                    {[10, 25, 50, 100, 500].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="pagination-center">
                <span className="pagination-info">
                  {totalItems === 0
                    ? '0 of 0'
                    : `${(currentPage - 1) * pageSize + 1}–${Math.min(
                        currentPage * pageSize,
                        totalItems
                      )} of ${totalItems}`}
                </span>
              </div>
              <div className="pagination-right">
                <button
                  className="btn-secondary"
                  disabled={!hasPreviousPage || loading}
                  onClick={() => hasPreviousPage && fetchTypes(currentPage - 1)}
                >
                  ‹
                </button>
                <button
                  className="btn-secondary"
                  disabled={!hasNextPage || loading}
                  onClick={() => hasNextPage && fetchTypes(currentPage + 1)}
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showViewModal && viewingType && (
        <div className="drawer-overlay" onClick={() => { setShowViewModal(false); setViewingType(null); }}>
          <div className="drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <h3 className="drawer-title">View Ambulance Type</h3>
              <button
                className="drawer-close"
                onClick={() => {
                  setShowViewModal(false);
                  setViewingType(null);
                }}
              >
                ×
              </button>
            </div>

            <div className="drawer-body" style={{ padding: '24px', overflowY: 'auto', maxHeight: 'calc(100vh - 120px)' }}>
              <div className="form-group">
                <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Display Name</label>
                <div
                  style={{
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    backgroundColor: '#f8f9fa',
                    minHeight: '40px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {viewingType.metadata.displayName || viewingType.metadata.name}
                </div>
              </div>

              <div className="form-group">
                <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Icon</label>
                <div
                  style={{
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    backgroundColor: '#f8f9fa',
                    minHeight: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                  }}
                >
                  {viewingType.spec.iconUrl ? (
                    <>
                      <img
                        src={viewingType.spec.iconUrl}
                        alt="Type icon"
                        style={{ width: 48, height: 48, objectFit: 'contain' }}
                      />
                      <span style={{ fontSize: 14, color: '#4b5563' }}>{viewingType.spec.iconUrl}</span>
                    </>
                  ) : (
                    <span style={{ color: '#999', fontStyle: 'italic' }}>Default map icon</span>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Description</label>
                <div
                  style={{
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    backgroundColor: '#f8f9fa',
                    minHeight: '60px',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {viewingType.spec.description || (
                    <span style={{ color: '#999', fontStyle: 'italic' }}>No description</span>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Created</label>
                <div
                  style={{
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    backgroundColor: '#f8f9fa',
                    minHeight: '40px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {formatDateDDMMYYYY(viewingType.metadata.createdAt)}
                </div>
              </div>
            </div>

            <div className="drawer-footer">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => {
                  setShowViewModal(false);
                  setViewingType(null);
                }}
              >
                Close
              </button>
              {hasPermission('ambulancetype.update') && (
                <button
                  type="button"
                  className="btn-submit"
                  onClick={() => {
                    // Open edit drawer with this type
                    handleOpenModal(viewingType);
                    setShowViewModal(false);
                    setViewingType(null);
                  }}
                >
                  Edit Type
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="drawer-overlay" onClick={() => setShowModal(false)}>
          <div className="drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <h3 className="drawer-title">
                {editingType ? 'Edit Ambulance Type' : 'Create New Ambulance Type'}
              </h3>
              <button className="drawer-close" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="drawer-form">
              <div className="drawer-body">
                {error && <div className="error-message">{error}</div>}

                <div className="form-group">
                  <label>
                    Display Name
                    <span className="required-star">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.displayName}
                    onChange={(e) => {
                      const cleaned = e.target.value.replace(/[^A-Za-z0-9 -]/g, '');
                      const capitalized = capitalizeWords(cleaned);
                      setFormData({ ...formData, displayName: capitalized });
                    }}
                    className={validationErrors.displayName ? 'input-error' : ''}
                    placeholder="e.g., Basic Life Support, Advanced Life Support, Critical Care"
                  />
                  {validationErrors.displayName && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      {validationErrors.displayName}
                    </small>
                  )}
                  <small style={{ color: '#666', fontSize: '14px' }}>
                    The name shown in the user interface. This can be edited later.
                  </small>
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Enter a description for this type (optional)"
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '16px',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      minHeight: '200px',
                    }}
                  />
                </div>

                <div className="form-group">
                  <label>Icon (PNG)</label>
                  <div className="avatar-upload-row">
                    <input
                      type="url"
                      value={formData.iconUrl}
                      onChange={(e) => setFormData({ ...formData, iconUrl: e.target.value })}
                      placeholder="https://example.com/ambulance-type-icon.png"
                      style={{ flex: '1 1 260px' }}
                    />
                    {!formData.iconUrl && (
                      <label
                        className={`avatar-upload-button ${iconUploading ? 'disabled' : ''}`}
                        style={{ marginBottom: 0 }}
                      >
                        <input
                          type="file"
                          accept="image/png"
                          style={{ display: 'none' }}
                          disabled={iconUploading}
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            if (!file.type.startsWith('image/')) {
                              setIconUploadError('Please select an image file.');
                              e.target.value = '';
                              return;
                            }
                            const maxBytes = 1 * 1024 * 1024;
                            if (file.size > maxBytes) {
                              const { formatFileSizeMB } = await import('../utils/imageCompression');
                              setIconUploadError(
                                `The selected image (${formatFileSizeMB(file.size)}) exceeds the maximum allowed size (1 MB). Please choose a smaller file.`
                              );
                              e.target.value = '';
                              return;
                            }
                            setIconUploadError('');
                            setIconUploading(true);
                            try {
                              const { compressImage } = await import('../utils/imageCompression');
                              const compressedFile = await compressImage(file, 72, 72, 0.9, 20);
                              const form = new FormData();
                              form.append('file', compressedFile);
                              form.append('folderPath', 'images/ambulance-types');
                              const res = await uploadFile(form);
                              const url = res.data?.data?.url;
                              if (url) {
                                setFormData((prev) => ({ ...prev, iconUrl: url }));
                                setIconUploadError('');
                              }
                            } catch (err: any) {
                              console.error('Failed to upload ambulance type icon', err);
                              setIconUploadError(err?.response?.data?.message || err?.message || 'Failed to upload icon.');
                            } finally {
                              setIconUploading(false);
                              e.target.value = '';
                            }
                          }}
                        />
                        {iconUploading ? (
                          <>
                            <span className="loading-spinner btn-spinner" aria-hidden style={{ marginRight: '8px' }} />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <span role="img" aria-label="upload" style={{ marginRight: '8px', display: 'inline-block' }}>
                              📤
                            </span>
                            Upload PNG
                          </>
                        )}
                      </label>
                    )}
                    {iconUploadError && (
                      <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                        {iconUploadError}
                      </small>
                    )}
                    {iconUploading && (
                      <small style={{ color: '#2563eb', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                        Processing & uploading icon...
                      </small>
                    )}
                    {formData.iconUrl && (
                      <button
                        type="button"
                        className="btn-secondary avatar-clear-button"
                        onClick={() => { setFormData((prev) => ({ ...prev, iconUrl: '' })); setIconUploadError(''); }}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <small style={{ color: '#666', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                    Optional: Upload a PNG or paste a URL. If left blank, the default ambulance map icon will be used. <span style={{ fontWeight: 600, color: '#374151' }}>Max size: 1MB.</span>
                  </small>
                  {formData.iconUrl && (
                    <div style={{ marginTop: '12px' }}>
                      <img
                        src={formData.iconUrl}
                        alt="Icon preview"
                        style={{ width: 48, height: 48, objectFit: 'contain' }}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="drawer-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit" disabled={submitting}>
                  {submitting ? (
                    <>
                      <span className="loading-spinner btn-spinner" aria-hidden />
                      {editingType ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    `${editingType ? 'Update' : 'Create'} Type`
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Delete Ambulance Type"
        message={`Are you sure you want to delete the ambulance type "${typeToDelete?.name}"? This action cannot be undone.`}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        type="danger"
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setTypeToDelete(null);
        }}
      />

      <ConfirmModal
        isOpen={showErrorModal}
        title="Error"
        message={errorMessage}
        confirmText="OK"
        cancelText=""
        type="danger"
        onConfirm={() => setShowErrorModal(false)}
        onCancel={() => setShowErrorModal(false)}
      />
    </>
  );

  if (skipLayout) {
    return content;
  }

  return <DashboardLayout>{content}</DashboardLayout>;
};

export default AmbulanceTypeManagement;
